import React, { useState, useEffect } from 'react';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

const SimpleLeadFilter = ({ onFilterChange, leads, employees = [] }) => {
  const [filterType, setFilterType] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadTitles, setLeadTitles] = useState([]);

  useEffect(() => {
    loadLeadStatuses();
    loadLeadSources();
    loadLeadTitles();
  }, []);

  const loadLeadStatuses = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-statuses');
      if (response.data.success) {
        setLeadStatuses(response.data.statuses || []);
      }
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  const loadLeadSources = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-sources');
      if (response.data.success) {
        setLeadSources(response.data.sources || []);
      }
    } catch (error) {
      console.error('Error loading sources:', error);
    }
  };

  const loadLeadTitles = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-titles');
      if (response.data.success) {
        setLeadTitles(response.data.titles || []);
      }
    } catch (error) {
      console.error('Error loading titles:', error);
    }
  };

  const filterTypes = [
    { value: 'employee', label: 'Employee' },
    { value: 'title', label: 'Title' },
    { value: 'status', label: 'Status' },
    { value: 'source', label: 'Source' },
    { value: 'date', label: 'Date' },
  ];

  const getFilterOptions = () => {
    switch (filterType) {
      case 'employee':
        return employees.map(emp => ({
          value: emp._id || emp.id,
          label: emp.name || emp.email
        }));
      
      case 'title':
        return leadTitles.map(title => ({
          value: title.name,
          label: title.name
        }));
      
      case 'status':
        return leadStatuses.map(status => ({
          value: status.name,
          label: status.label
        }));
      
      case 'source':
        return leadSources.map(source => ({
          value: source.name,
          label: source.label
        }));
      
      case 'date':
        return [
          { value: 'today', label: 'Today' },
          { value: 'yesterday', label: 'Yesterday' },
          { value: 'last7days', label: 'Last 7 Days' },
          { value: 'last30days', label: 'Last 30 Days' },
          { value: 'thisMonth', label: 'This Month' },
          { value: 'lastMonth', label: 'Last Month' }
        ];
      
      default:
        return [];
    }
  };

  const handleAddFilter = (selectedValue) => {
    const valToUse = selectedValue || filterValue;
    if (!filterType || !valToUse) return;

    const filterTypeLabel = filterTypes.find(ft => ft.value === filterType)?.label || filterType;
    const filterValueLabel = getFilterOptions().find(opt => opt.value === valToUse)?.label || valToUse;

    const newFilter = {
      id: Date.now(),
      type: filterType,
      value: valToUse,
      typeLabel: filterTypeLabel,
      valueLabel: filterValueLabel
    };

    const updatedFilters = [...activeFilters, newFilter];
    setActiveFilters(updatedFilters);
    
    // Reset selections
    setFilterType('');
    setFilterValue('');

    // Notify parent
    onFilterChange(updatedFilters);
  };

  const handleRemoveFilter = (filterId) => {
    const updatedFilters = activeFilters.filter(f => f.id !== filterId);
    setActiveFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleClearAll = () => {
    setActiveFilters([]);
    setFilterType('');
    setFilterValue('');
    onFilterChange([]);
  };

  return (
    <>
      {/* ── Desktop Layout: single inline row ─────────────────── */}
      <div className="hidden md:flex items-center gap-2">
        {/* Filter Type */}
        <select
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setFilterValue('');
          }}
          className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">1. Select Type...</option>
          {filterTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>

        {/* Filter Value */}
        <select
          value={filterValue}
          onChange={(e) => {
            const val = e.target.value;
            setFilterValue(val);
            if (val) handleAddFilter(val);
          }}
          disabled={!filterType}
          className="px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">{filterType ? 'Select value...' : 'Select type first'}</option>
          {getFilterOptions().map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        {/* Active filter badge + clear */}
        {activeFilters.length > 0 && (
          <>
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
              <FunnelIcon className="h-3 w-3 text-blue-600" />
              <span className="text-blue-700 font-medium">{activeFilters.length}</span>
            </div>
            <button
              onClick={handleClearAll}
              className="text-xs text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
            >
              Clear All
            </button>
          </>
        )}
      </div>

      {/* ── Mobile Layout: compact 2-col grid row ─────────────── */}
      <div className="md:hidden w-full flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2">
          {/* Filter Type */}
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setFilterValue('');
            }}
            style={{ fontSize: '13px' }}
            className="w-full px-2 py-2 border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-700 truncate"
          >
            <option value="">Filter Type...</option>
            {filterTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          {/* Filter Value */}
          <select
            value={filterValue}
            onChange={(e) => {
              const val = e.target.value;
              setFilterValue(val);
              if (val) handleAddFilter(val);
            }}
            disabled={!filterType}
            style={{ fontSize: '13px' }}
            className="w-full px-2 py-2 border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-700 truncate disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{filterType ? 'Select value...' : 'Select type first'}</option>
            {getFilterOptions().map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Active filter count + clear — only when filters active */}
        {activeFilters.length > 0 && (
          <div className="flex items-center justify-between px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-1.5">
              <FunnelIcon className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-blue-700 text-xs font-semibold">
                {activeFilters.length} filter{activeFilters.length > 1 ? 's' : ''} active
              </span>
            </div>
            <button
              onClick={handleClearAll}
              className="text-xs text-red-600 hover:text-red-700 font-semibold"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* ── Active Filter Tags (shared across both views) ────── */}
      {activeFilters.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {activeFilters.map(filter => (
            <div
              key={filter.id}
              className="inline-flex items-center bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5 text-xs"
            >
              <span className="text-blue-700 font-semibold">{filter.typeLabel}:</span>
              <span className="text-blue-900 ml-1">{filter.valueLabel}</span>
              <button
                onClick={() => handleRemoveFilter(filter.id)}
                className="ml-1.5 text-blue-500 hover:text-red-600 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default SimpleLeadFilter;
